import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Loader2, Percent, RotateCcw, Info, Search } from 'lucide-react';

/**
 * CommissionMatrixPage — admin sets per-(tenant, game) commission overrides.
 *
 * Precedence the backend resolver uses:
 *   per_staff  →  per_tenant_game (this matrix)  →  per_tenant  →  platform_default
 *
 * Each cell is an input: blank = fall back to tenant/platform default,
 * number = explicit override. Changes commit on blur + Enter.
 */
export default function CommissionMatrixPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [draft, setDraft] = useState({}); // key -> local unsaved value
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/commissions');
      setData(r.data);
      setDraft({});
    } catch {
      toast.error('Failed to load commission matrix');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const key = (tenantId, gameId) => `${tenantId || 'main'}::${gameId}`;

  const cellValue = (tenantId, gameId) => {
    const k = key(tenantId, gameId);
    if (k in draft) return draft[k];
    const cell = data?.cells?.[k];
    return cell ? String(cell.commission_percent) : '';
  };

  const commit = async (tenant, game) => {
    const k = key(tenant.id, game.id);
    const raw = draft[k];
    if (raw === undefined) return;
    const trimmed = String(raw).trim();
    setSaving((s) => ({ ...s, [k]: true }));
    try {
      if (trimmed === '') {
        await api.delete('/api/admin/commissions/cell', { data: { tenant_id: tenant.id, game_id: game.id } });
        toast.success(`Cleared ${tenant.slug} × ${game.name}`);
      } else {
        const n = Number(trimmed);
        if (Number.isNaN(n) || n < 0 || n > 100) {
          toast.error('Commission must be 0 – 100');
          setSaving((s) => ({ ...s, [k]: false }));
          return;
        }
        await api.put('/api/admin/commissions/cell', {
          tenant_id: tenant.id, game_id: game.id, commission_percent: n,
        });
        toast.success(`${tenant.slug} × ${game.name} → ${n}%`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving((s) => ({ ...s, [k]: false }));
    }
  };

  const filteredGames = useMemo(() => {
    if (!data) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return data.games;
    return data.games.filter(
      (g) => (g.name || '').toLowerCase().includes(q) || (g.code || '').toLowerCase().includes(q),
    );
  }, [data, filter]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-400 p-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading commission matrix…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="commission-matrix-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Percent className="w-6 h-6 text-[#D4AF37]" /> Commission Matrix
        </h1>
        <p className="text-xs text-gray-500 mt-1 max-w-3xl leading-5">
          Set referral-agent commission <b>per (tenant × game)</b>. Blank cells fall back to the tenant rate shown in
          each column header, or the platform default ({data.platform_default_percent}%) if the tenant itself has no
          override. Staff-level overrides — configured on the Referral Staff page — still take precedence over
          everything here.
        </p>
      </div>

      <div className="rounded-lg bg-[#0f1320] border border-white/10 p-3 text-xs text-gray-400 flex items-center gap-2">
        <Info className="w-3.5 h-3.5 text-blue-400 shrink-0" />
        Precedence: <span className="text-blue-300 font-mono">per-staff</span> →
        <span className="text-[#D4AF37] font-mono">per-(tenant, game)</span> →
        <span className="text-purple-300 font-mono">per-tenant</span> →
        <span className="text-gray-300 font-mono">platform-default</span>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            className="w-full pl-8 pr-3 py-2 bg-[#0f1320] border border-white/10 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-[#D4AF37]/50"
            placeholder="Filter games…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="commissions-game-filter"
          />
        </div>
        <button
          onClick={load}
          className="text-xs text-gray-400 hover:text-blue-400 flex items-center gap-1"
          data-testid="commissions-refresh"
        >
          <RotateCcw className="w-3 h-3" /> Refresh
        </button>
        <div className="text-xs text-gray-500 ml-auto">
          {Object.keys(data.cells).length} active override{Object.keys(data.cells).length === 1 ? '' : 's'}
        </div>
      </div>

      <div className="overflow-auto rounded-xl border border-white/10" data-testid="commission-matrix-table">
        <table className="min-w-full text-sm border-collapse">
          <thead className="bg-[#131822]">
            <tr>
              <th className="text-left px-3 py-2 sticky left-0 bg-[#131822] z-10 border-r border-white/5">
                Game \ Tenant
              </th>
              {data.tenants.map((t) => (
                <th key={t.slug} className="px-3 py-2 text-center min-w-[120px]">
                  <div className="flex flex-col items-center">
                    <span className="text-gray-200 font-semibold text-xs">
                      {t.slug === 'main' ? '★ ' : ''}{t.name}
                    </span>
                    <span className="text-[10px] text-gray-500">
                      fallback {Number(t.tenant_rate || 0)}%
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredGames.map((g) => (
              <tr key={g.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-3 py-2 sticky left-0 bg-[#0b0f1a] border-r border-white/5">
                  <div className="text-gray-200 font-medium">{g.name}</div>
                  <div className="text-[10px] text-gray-500">{g.provider_code || g.code}</div>
                </td>
                {data.tenants.map((t) => {
                  const k = key(t.id, g.id);
                  const val = cellValue(t.id, g.id);
                  const isOverridden = val !== '';
                  const isSaving = saving[k];
                  return (
                    <td key={k} className="px-2 py-1.5 text-center">
                      <div className="relative inline-flex items-center">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          className={`w-20 text-center px-2 py-1 text-xs rounded-md border outline-none tabular-nums ${
                            isOverridden
                              ? 'bg-[#D4AF37]/10 border-[#D4AF37]/40 text-[#F5D76E] font-semibold'
                              : 'bg-[#0f1320] border-white/10 text-gray-500'
                          } focus:border-[#D4AF37]`}
                          placeholder="–"
                          value={val}
                          disabled={isSaving}
                          onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                          onBlur={() => commit(t, g)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (e.key === 'Escape') {
                              setDraft((d) => { const n = { ...d }; delete n[k]; return n; });
                            }
                          }}
                          data-testid={`commission-cell-${t.slug}-${g.code || g.id}`}
                        />
                        <span className="ml-1 text-[10px] text-gray-500">%</span>
                        {isSaving && <Loader2 className="w-3 h-3 animate-spin text-[#D4AF37] absolute -right-4" />}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            {filteredGames.length === 0 && (
              <tr>
                <td colSpan={data.tenants.length + 1} className="text-center text-gray-500 py-8">
                  No games match “{filter}”
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
